import { AlertTriangle, Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import { useStatusChangeForm } from "@/hooks/useStatusChangeForm";

const ALL_STATUSES = ["New", "Under Care", "Released", "Adopted", "Sanctuary Resident", "Passed Away"];

export default function StatusChangeDialog({ open, onOpenChange, caseItem, onUpdated }) {
  const {
    target, setTarget,
    note, setNote,
    photos, setPhotos,
    date, setDate,
    confirmed, setConfirmed,
    saving,
    handleOpenChange,
    submit,
  } = useStatusChangeForm(caseItem.id, onUpdated, onOpenChange);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="status-change-dialog" className="rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Change Status</DialogTitle>
        </DialogHeader>

        {!target && (
          <div className="grid gap-2 py-2">
            <p className="text-xs text-amtm-textMuted mb-1">Select a status to correct or update this case. Current status is marked.</p>
            {ALL_STATUSES.map((status) => {
              const isCurrent = status === caseItem.status;
              return (
                <button
                  key={status}
                  onClick={() => !isCurrent && setTarget(status)}
                  disabled={isCurrent}
                  data-testid={`status-option-${status.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`h-14 rounded-xl border-2 flex items-center justify-between px-4 font-semibold text-left transition-colors ${
                    isCurrent
                      ? "border-amtm-teal bg-amtm-teal/10 text-amtm-teal cursor-default"
                      : "border-amtm-border hover:border-amtm-teal"
                  }`}
                >
                  {status}
                  {isCurrent && (
                    <span className="flex items-center gap-1 text-xs font-medium">
                      <Check className="h-3.5 w-3.5" /> Current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {target && target === "Passed Away" && !confirmed && (
          <div className="space-y-4 py-2" data-testid="passed-away-confirm-step">
            <div className="flex items-start gap-3 bg-amtm-textMuted/10 rounded-xl p-4">
              <AlertTriangle className="h-5 w-5 text-amtm-textMuted shrink-0 mt-0.5" />
              <p className="text-sm text-amtm-text">
                Are you sure? This marks <strong>{caseItem.animal_name}</strong> as passed away. We know this is difficult — take a moment before confirming.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTarget(null)} data-testid="passed-away-cancel-button">Cancel</Button>
              <Button onClick={() => setConfirmed(true)} className="bg-amtm-textMuted hover:bg-amtm-textMuted/90" data-testid="passed-away-confirm-button">
                Yes, I'm sure
              </Button>
            </DialogFooter>
          </div>
        )}

        {target && (target !== "Passed Away" || confirmed) && (
          <div className="space-y-4 py-2">
            {(target === "Released" || target === "Adopted" || target === "Sanctuary Resident") && (
              <div>
                <Label className="mb-2 block">Recovery Photo (optional)</Label>
                <PhotoUpload photos={photos} setPhotos={setPhotos} multiple={false} testId="status-photo-upload" />
              </div>
            )}
            <div>
              <Label htmlFor="status-date">Date of this event</Label>
              <Input
                id="status-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="status-date-input"
                className="mt-1.5 h-11"
              />
              <p className="text-xs text-amtm-textMuted mt-1">Defaults to today — set an earlier date if you're back-filling a past event.</p>
            </div>
            <div>
              <Label htmlFor="status-note">Note (optional)</Label>
              <Textarea
                id="status-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={target === "Passed Away" ? "A gentle note about their final days..." : "Add a note (e.g. correcting an earlier mistake)..."}
                data-testid="status-note-input"
                className="mt-1.5"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setTarget(null); setConfirmed(false); }} data-testid="status-back-button">Back</Button>
              <Button onClick={() => submit(target)} disabled={saving} className="bg-amtm-teal text-white hover:bg-amtm-tealHover" data-testid="status-confirm-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Confirm: ${target}`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
